import '../i18n/autoTranslate';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { InterviewType, CodingMode, InterviewStatus } from "./types";

import {
  Code,
  Server,
  BookOpen,
  Brain,
  Terminal,
  Activity,
  ShieldCheck,
  Clock,
  Award,
  Sparkles,
} from "lucide-react";

import {
  buildIntro,
  checkIfCanUseInterview,
  createInterviewSession,
  analyzeTurn,
  finalAnalysis,
  turnAnalyzeToUI,
  finalAnalysisToFeedbackData,
  generateTechnicalQuestions,
  submitInterviewResults,
  
  // ✅ NEW (must exist in interviewBackend.js)
  runCode,
  submitCode,
  checkpointCodeDraft,
  loadCodeDraft,
  formatPythonCode,
} from "../../services/interviewBackend";

import CodeEditor from "./CodeEditor";
import ChatPanel from "./ChatPanel";

import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import SEO from "../SEO";
import FinalResultsModal from "./FinalResultsModal";
import {
  saveInterviewResults,
  buildFinalAnswerPayload
} from "../../services/interviewBackend";

const APP_NAME = "SignalForge AI";

/* ----------------------------- UI Primitives ----------------------------- */

const AppBackground = ({ children }) => (
  <div className="min-h-screen w-full bg-[#07090c] text-white relative overflow-hidden">
    <div className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-emerald-400/10 blur-[80px]" />
      <div className="absolute -bottom-28 -right-24 h-[560px] w-[560px] rounded-full bg-sky-400/10 blur-[90px]" />
      <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_65%_20%,rgba(255,255,255,0.10),transparent_55%)]" />
      <div className="absolute inset-0 opacity-35 [background-image:radial-gradient(rgba(255,255,255,0.08)_1px,transparent_1px)] [background-size:26px_26px]" />
      <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:92px_92px]" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-black/70" />
    </div>
    <div className="relative">{children}</div>
  </div>
);

const Glass = ({ children, className = "" }) => (
  <div
    className={[
      "rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl",
      "shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]",
      className,
    ].join(" ")}
  >
    {children}
  </div>
);

const PrimaryButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "px-5 py-2.5 rounded-2xl font-semibold text-sm",
      "bg-white text-black hover:bg-white/90",
      "shadow-[0_12px_30px_-18px_rgba(255,255,255,0.50)]",
      "transition disabled:opacity-50 disabled:hover:bg-white",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const GhostButton = ({ children, onClick, disabled, className = "" }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={[
      "px-5 py-2.5 rounded-2xl font-semibold text-sm",
      "border border-white/12 bg-white/5 hover:bg-white/8",
      "text-white/85 transition disabled:opacity-50",
      className,
    ].join(" ")}
  >
    {children}
  </button>
);

const Pill = ({ children, tone = "neutral" }) => {
  const cls =
    tone === "emerald"
      ? "border-emerald-400/20 bg-emerald-400/10 text-emerald-100"
      : tone === "sky"
      ? "border-sky-400/20 bg-sky-400/10 text-sky-100"
      : tone === "amber"
      ? "border-amber-300/25 bg-amber-300/10 text-amber-100"
      : "border-white/12 bg-white/6 text-white/80";

  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold tracking-wide",
        cls,
      ].join(" ")}
    >
      {children}
    </span>
  );
};

const TopBar = ({ onHome = () => {} }) => (
  <div className="flex items-center justify-between px-6 py-5 mt-20">
    <button onClick={onHome} className="group inline-flex items-center gap-2" title={APP_NAME}>
      <div className="h-9 w-9 rounded-2xl bg-white/8 border border-white/12 backdrop-blur-xl flex items-center justify-center">
        <Sparkles className="h-4.5 w-4.5 text-white/85" />
      </div>
      <div className="leading-tight">
        <div className="text-sm font-semibold tracking-tight">Technical Interview Suite</div>
        {/* <div className="text-[11px] text-white/55 -mt-0.5"></div> */}
      </div>
    </button>

    <div className="hidden md:flex items-center gap-2">
      <Pill tone="emerald">Live</Pill>
      <Pill tone="sky">System Design</Pill>
      <Pill tone="amber">Deep Dive</Pill>
    </div>

    <div className="flex items-center gap-2">
      <PrimaryButton className="hidden sm:inline-flex" onClick={() => {}}>Docs</PrimaryButton>
      {/* <PrimaryButton onClick={() => {}}>Create Account</PrimaryButton> */}
    </div>
  </div>
);

/* ----------------------------- Dashboard ----------------------------- */

const Dashboard = ({ onStart }) => {
  const handleCardClick = (type) => {
    if (type === InterviewType.CODING) {
      const options = [
        { mode: CodingMode.LIVE, topic: "Algorithms" },
        { mode: CodingMode.TIMED, topic: "Data Structures" },
        { mode: CodingMode.DEBUGGING, topic: "React Hooks" },
      ];
      const selected = options[Math.floor(Math.random() * options.length)];
      onStart(type, selected.mode, selected.topic);
      return;
    }

    if (type === InterviewType.SYSTEM_DESIGN) {
      const topics = ["Social Media Feed", "Chat Application", "URL Shortener"];
      onStart(type, undefined, topics[Math.floor(Math.random() * topics.length)]);
      return;
    }

    const topics = ["React Internals", "Database Indexing", "Web Security (JWT/OAuth)"];
    onStart(type, undefined, topics[Math.floor(Math.random() * topics.length)]);
  };

  return (
    <AppBackground>
      <TopBar />
      <div className="mx-auto max-w-7xl px-6 pb-10 pt-6 ">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <div className="space-y-5">
              <Pill>
                <Sparkles className="h-3.5 w-3.5" />
                Interview-grade signal
              </Pill>

              <h1 className="text-[40px] leading-[1.05] sm:text-[52px] font-semibold tracking-tight">
                Practice technical interviews
                <span className="text-white/60"> with real pressure & clarity.</span>
              </h1>

              <p className="text-white/65 text-base sm:text-lg leading-relaxed max-w-xl">
                Live coding, timed challenges, system design, and deep dives — with scoring to make you ready for the loop.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <PrimaryButton onClick={() => handleCardClick(InterviewType.CODING)}>
                  Start Technical Session
                </PrimaryButton>
                <GhostButton onClick={() => handleCardClick(InterviewType.SYSTEM_DESIGN)}>
                  Explore System Design
                </GhostButton>
              </div>

              <div className="flex flex-wrap gap-2 pt-2">
                <Pill tone="emerald">Signal Metrics</Pill>
                <Pill tone="sky">Realtime Chat</Pill>
                <Pill tone="neutral">Code Context</Pill>
                <Pill tone="amber">Report Radar</Pill>
              </div>
            </div>
          </div>
          <SEO
            title="Technical Interview Practice with AI — InterviewGenieAI"
            description="Prepare for technical interviews with AI-powered practice, role-based questions, instant feedback, answer scoring, and progress tracking."
            canonical="https://interviewgenieai.com/technical-interview-practice"
          />
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button onClick={() => handleCardClick(InterviewType.CODING)} className="text-left group">
                <Glass className="p-6 h-full relative overflow-hidden hover:bg-white/[0.06] transition">
                  <div className="absolute -top-16 -right-16 h-44 w-44 rounded-full bg-emerald-400/10 blur-[55px]" />
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="h-12 w-12 rounded-2xl border border-white/12 bg-white/6 flex items-center justify-center">
                        <Terminal className="h-5 w-5 text-emerald-200" />
                      </div>
                      <div className="mt-4">
                        <div className="text-lg font-semibold tracking-tight">Coding</div>
                        <div className="text-sm text-white/60 mt-1">Live • Timed • Debugging</div>
                      </div>
                    </div>
                    <div className="opacity-60 group-hover:opacity-100 transition">
                      <Code className="h-6 w-6" />
                    </div>
                  </div>

                  <div className="mt-5 space-y-2 text-sm text-white/70">
                    <div className="flex items-center gap-2">
                      <Activity className="h-4 w-4 text-emerald-300/90" />
                      Live coding loop
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-amber-200/90" />
                      Timed pressure mode
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-rose-200/90" />
                      Debugging scenario
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10 text-sm font-semibold text-white/80">
                    Start session →
                  </div>
                </Glass>
              </button>

              <button onClick={() => handleCardClick(InterviewType.SYSTEM_DESIGN)} className="text-left group">
                <Glass className="p-6 h-full relative overflow-hidden hover:bg-white/[0.06] transition">
                  <div className="absolute -top-20 -left-20 h-52 w-52 rounded-full bg-sky-400/10 blur-[60px]" />
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="h-12 w-12 rounded-2xl border border-white/12 bg-white/6 flex items-center justify-center">
                        <Server className="h-5 w-5 text-sky-200" />
                      </div>
                      <div className="mt-4">
                        <div className="text-lg font-semibold tracking-tight">System Design</div>
                        <div className="text-sm text-white/60 mt-1">Scale • Trade-offs • Failure modes</div>
                      </div>
                    </div>
                    <div className="opacity-60 group-hover:opacity-100 transition">
                      <Brain className="h-6 w-6" />
                    </div>
                  </div>

                  <div className="mt-5 space-y-2 text-sm text-white/70">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-sky-200/90" />
                      Social feed
                    </div>
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-sky-200/90" />
                      Chat application
                    </div>
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-sky-200/90" />
                      URL shortener
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10 text-sm font-semibold text-white/80">
                    Start session →
                  </div>
                </Glass>
              </button>

              <button onClick={() => handleCardClick(InterviewType.DEEP_DIVE)} className="text-left group">
                <Glass className="p-6 h-full relative overflow-hidden hover:bg-white/[0.06] transition">
                  <div className="absolute -bottom-24 -right-24 h-60 w-60 rounded-full bg-white/6 blur-[70px]" />
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="h-12 w-12 rounded-2xl border border-white/12 bg-white/6 flex items-center justify-center">
                        <BookOpen className="h-5 w-5 text-white/85" />
                      </div>
                      <div className="mt-4">
                        <div className="text-lg font-semibold tracking-tight">Deep Dive</div>
                        <div className="text-sm text-white/60 mt-1">Internals • Databases • Security</div>
                      </div>
                    </div>
                    <div className="opacity-60 group-hover:opacity-100 transition">
                      <Award className="h-6 w-6" />
                    </div>
                  </div>

                  <div className="mt-5 space-y-2 text-sm text-white/70">
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-white/75" />
                      React internals
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-white/75" />
                      Databases & SQL
                    </div>
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4 text-white/75" />
                      Web security
                    </div>
                  </div>

                  <div className="mt-6 pt-4 border-t border-white/10 text-sm font-semibold text-white/80">
                    Start session →
                  </div>
                </Glass>
              </button>
            </div>

            <div className="mt-4">
              <Glass className="p-5">
                <div className="flex flex-col  sm:items-left  gap-3">
                  <div className="text-sm text-white/70">
                    Tip: structure beats speed. Explain → edge cases → complexity → code.
                  </div>
                  <div className="flex items-center gap-2 w-1/2">
                    <Pill tone="emerald">Clarity</Pill>
                    <Pill tone="sky">Correctness</Pill>
                    <Pill>Trade-offs</Pill>
                  </div>
                </div>
              </Glass>
            </div>
          </div>
        </div>
      </div>
    </AppBackground>
  );
};

/* ----------------------------- Feedback Report ----------------------------- */

const FeedbackReport = ({ feedback, onReset }) => {
  const seniorityColor =
    feedback.seniority === "Staff"
      ? "text-amber-200"
      : feedback.seniority === "Senior"
      ? "text-sky-200"
      : feedback.seniority === "Mid-Level"
      ? "text-emerald-200"
      : "text-white/85";

  return (
    <AppBackground>
      <TopBar onHome={onReset} />
      <div className="mx-auto max-w-6xl px-6 pb-12">
        <Glass className="p-7 sm:p-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b border-white/10 pb-6">
            <div>
              <div className="text-xs tracking-widest text-white/55 uppercase">Interview Report</div>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight">Performance analysis</h2>
              <p className="mt-2 text-white/65">Radar overview + strengths and improvement points.</p>
            </div>

            <div className="text-right">
              <div className="text-[11px] tracking-widest text-white/55 uppercase">Estimated Seniority</div>
              <div className={`mt-2 text-2xl font-semibold ${seniorityColor}`}>{feedback.seniority} Engineer</div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5">
              <Glass className="p-5 h-full">
                <div className="text-sm font-semibold text-white/80 mb-4">Skill Radar</div>
                <div className="min-h-[320px]">
                  <ResponsiveContainer width="100%" height={320}>
                    <RadarChart cx="50%" cy="50%" outerRadius="80%" data={feedback.radarData || []}>
                      <PolarGrid stroke="rgba(255,255,255,0.10)" />
                      <PolarAngleAxis dataKey="subject" tick={{ fill: "rgba(255,255,255,0.65)", fontSize: 12 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                      <Radar
                        name="Candidate"
                        dataKey="A"
                        stroke="rgba(125,211,252,0.95)"
                        strokeWidth={2.5}
                        fill="rgba(125,211,252,0.20)"
                        fillOpacity={0.9}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </Glass>
            </div>

            <div className="lg:col-span-7 space-y-5">
              <Glass className="p-6">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <div className="text-xs tracking-widest text-white/55 uppercase">Overall score</div>
                    <div className="mt-1 text-4xl font-semibold tracking-tight">
                      {feedback.score}
                      <span className="text-white/40 text-xl font-medium">/100</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pill tone="emerald">Correctness</Pill>
                    <Pill tone="sky">Clarity</Pill>
                    <Pill>Depth</Pill>
                  </div>
                </div>
                <p className="mt-4 text-white/70 leading-relaxed">{feedback.summary}</p>
              </Glass>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Glass className="p-5">
                  <div className="text-sm font-semibold text-emerald-200 mb-3">Strengths</div>
                  <ul className="list-disc list-inside text-sm text-white/70 space-y-1.5">
                    {(feedback.strengths || []).slice(0, 6).map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </Glass>

                <Glass className="p-5">
                  <div className="text-sm font-semibold text-rose-200 mb-3">Areas to Improve</div>
                  <ul className="list-disc list-inside text-sm text-white/70 space-y-1.5">
                    {(feedback.weaknesses || []).slice(0, 6).map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </Glass>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <PrimaryButton onClick={onReset}>Start New Interview</PrimaryButton>
                <GhostButton
                  onClick={() =>
                    navigator.clipboard?.writeText(JSON.stringify(feedback, null, 2)).catch(() => {})
                  }
                >
                  Copy JSON
                </GhostButton>
              </div>
            </div>
          </div>
        </Glass>
      </div>
    </AppBackground>
  );
};

/* ----------------------------- Main Tech App ----------------------------- */

const DEFAULT_METRICS = { correctness: 0, clarity: "Medium" };

function safeWordsCount(text) {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

function estimateDurationSec(text) {
  const words = safeWordsCount(text);
  // ~130 wpm => 2.16 wps, clamp for stability
  return Math.max(3, Math.min(90, Math.round(words / 2.2)));
}

function storageKeyFor(sessionId) {
  return `ig_code_draft_v1:${sessionId || "local"}`;
}

function getFinalInterviewConfiguration() {
  try {
    const raw = localStorage.getItem("finalInterviewConfiguration");
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function pickConfiguredTopic(type, fallbackTopic) {
  const cfg = getFinalInterviewConfiguration();
  return (
    cfg.selectedRole ||
    cfg.selectedIndustry ||
    cfg.interviewType ||
    cfg.selectedType ||
    fallbackTopic ||
    (type === InterviewType.CODING ? "Technical coding" : "Technical interview")
  );
}

function questionText(q) {
  if (!q) return "";
  if (typeof q === "string") return q.trim();
  return String(q.text || q.question || q.prompt || q.title || "").trim();
}

function questionLabelFor(stateLike) {
  const q = stateLike.questions?.[stateLike.currentQuestionIndex];
  return questionText(q) || stateLike.topic || "Technical interview question";
}

function nextQuestionMessage(questions, nextIndex) {
  const next = questions?.[nextIndex];
  const text = questionText(next);
  if (!text) return "";
  return `Next question (${nextIndex + 1}/${questions.length}):\n${text}`;
}

function normalizeRunResult(res) {
  if (!res) return null;
  return res.run || res.result || res;
}

function buildTechnicalAnswerPayload({ state, answer, includeCodeContext = false, runResult = null }) {
  const question = questionLabelFor(state);
  const parts = [
    `QUESTION:\n${question}`,
    `ANSWER:\n${answer || ""}`,
  ];

  if (includeCodeContext || state.type === InterviewType.CODING) {
    parts.push(`LANGUAGE:\n${state.language || "typescript"}`);
    parts.push(`CODE:\n${state.code || ""}`);
    if (state.tests) parts.push(`TESTS:\n${state.tests}`);
    if (state.notes) parts.push(`CANDIDATE NOTES:\n${state.notes}`);
    if (runResult) parts.push(`EXECUTION RESULT:\n${JSON.stringify(runResult, null, 2)}`);
  }

  if (state.type === InterviewType.SYSTEM_DESIGN && state.designNotes) {
    parts.push(`SYSTEM DESIGN NOTES:\n${state.designNotes}`);
  }

  return parts.join("\n\n---\n\n");
}

export default function Tech() {
  const [state, setState] = useState({
    type: InterviewType.CODING,
    mode: CodingMode.LIVE,
    topic: "",
    messages: [],
    code: "",
    // ✅ NEW
    tests: "",
    notes: "",
    designNotes: "",
    startTime: 0,
    duration: 0,
    status: InterviewStatus.IDLE,
    language: "typescript",
    metrics: DEFAULT_METRICS,
    interview_id: null,
    session_id: null,
    questions: [],
    currentQuestionIndex: 0,
    generated_questions_id: null,
    questionsUsedFallback: false,
  });

 // ✅ NEW: run state for real run/compile/tests console
  const [runState, setRunState] = useState({
    running: false,
    runningMs: 0,
    lastRun: null,
  });
  
  // Patch your technical interview component with this structure.

// State:
const [completedAnswers, setCompletedAnswers] = useState([]);
const [showResultsModal, setShowResultsModal] = useState(false);
const [finalizing, setFinalizing] = useState(false);
const [finalFeedbackData, setFinalFeedbackData] = useState(null);
const [finalError, setFinalError] = useState("");

// Final results modal state. The modal is opened by handleEndInterview.
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const analyzedTurnsRef = useRef([]);
  const inFlightRef = useRef(false);
  const runTimerRef = useRef(null);

  // Stable timer for duration (seconds)
  useEffect(() => {
    if (state.status !== InterviewStatus.ACTIVE || !state.startTime) return;

    const t = setInterval(() => {
      setState((prev) => {
        const dur = Math.max(0, Math.floor((Date.now() - prev.startTime) / 1000));
        return prev.duration === dur ? prev : { ...prev, duration: dur };
      });
    }, 1000);

    return () => clearInterval(t);
  }, [state.status, state.startTime]);

  const headerIcon = useMemo(() => {
    if (state.type === InterviewType.CODING) return Terminal;
    if (state.type === InterviewType.SYSTEM_DESIGN) return Server;
    return BookOpen;
  }, [state.type]);

  const isDeepDive = state.type === InterviewType.DEEP_DIVE;
  const HeaderIcon = headerIcon;


  // ACTIVE screen
  const progressPct = useMemo(() => {
    // 30 min target (same as createInterviewSession duration), clamp 0-100
    const pct = Math.round((state.duration / (30 * 60)) * 100);
    return Math.max(0, Math.min(100, pct));
  }, [state.duration]);

  const handleStartInterview = async (type, mode, topic) => {
    if (loading) return;

    setLoading(true);
    setFeedback(null);
    setFinalError("");
    setFinalFeedbackData(null);
    setShowResultsModal(false);
    analyzedTurnsRef.current = [];

    try {
      // Consumes/validates interview quota before creating a real session.
      await checkIfCanUseInterview();
    } catch (error) {
      const message =
        error?.message ||
        "You cannot start a new interview right now. Please check your subscription or interview usage.";

      console.error("[technical interview] usage check failed", error);
      setLoading(false);

      setState((prev) => ({
        ...prev,
        status: InterviewStatus.IDLE,
        messages: [],
        startTime: 0,
        duration: 0,
      }));

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("interview:usage-denied", {
            detail: { message },
          })
        );
      }

      return;
    }

    const storedConfig = getFinalInterviewConfiguration();
    const configuredTopic = pickConfiguredTopic(type, topic);

    const initialCode =
      type === InterviewType.CODING
        ? `// ${configuredTopic || "Technical challenge"}
// Wait for the AI question, then write your solution here.

`
        : "";

    setState({
      type,
      mode,
      topic: configuredTopic,
      messages: [
        {
          id: "init-loading",
          role: "ai",
          content: "Creating your interview session and generating real AI questions…",
          timestamp: Date.now(),
        },
      ],
      code: initialCode,
      tests: "",
      notes: "",
      designNotes: "",
      startTime: Date.now(),
      duration: 0,
      status: InterviewStatus.ACTIVE,
      language: "typescript",
      metrics: { correctness: 50, clarity: "Medium" },
      interview_id: null,
      session_id: null,
      questions: [],
      currentQuestionIndex: 0,
      generated_questions_id: null,
      questionsUsedFallback: false,
    });

    let sessionMeta = null;
    try {
      sessionMeta = await createInterviewSession({ type, duration: 30, selectedFormat: "chat" });
    } catch (e) {
      console.error("[technical interview] create session failed", e);
    }

    let generated = null;
    let questions = [];
    try {
      generated = await generateTechnicalQuestions({
        interviewId: sessionMeta?.interview_id,
        type,
        mode,
        topic: configuredTopic,
        questionCount: type === InterviewType.CODING ? 4 : 5,
        difficulty: "medium",
        role: storedConfig.selectedRole || (type === InterviewType.CODING ? "Software Engineer" : "Technical Candidate"),
        industry: storedConfig.selectedIndustry || "Technology",
        difficultyLevel: 3,
        tone: 3,
      });

      if (generated?.used_fallback) {
        throw new Error("Backend returned fallback/mock questions instead of real AI questions.");
      }
      questions = generated?.normalizedQuestions || [];
    } catch (e) {
      console.error("[technical interview] question generation failed", e);
    }

    if (!questions.length) {
      setState((prev) => ({
        ...prev,
        interview_id: sessionMeta?.interview_id ?? prev.interview_id,
        session_id: sessionMeta?.session_id ?? prev.session_id,
        messages: [
          {
            id: "init-error",
            role: "ai",
            content:
              "I could not generate real AI questions from the backend, so I stopped the session instead of showing mock data. Check Groq/API keys, model parser, and /api/interview/generate-questions logs, then restart this session.",
            timestamp: Date.now(),
          },
        ],
      }));
      setLoading(false);
      return;
    }

    const firstQuestion = questionText(questions[0]);
    const intro = [
      buildIntro(type, mode, topic),
      "",
      "Questions generated by AI for this session.",
      "",
      `Question 1/${questions.length}:`,
      firstQuestion,
    ].filter(Boolean).join("\n");

    setState((prev) => ({
      ...prev,
      interview_id: sessionMeta?.interview_id ?? prev.interview_id,
      session_id: sessionMeta?.session_id ?? prev.session_id,
      generated_questions_id: generated?.generated_questions_id ?? null,
      questions,
      currentQuestionIndex: 0,
      questionsUsedFallback: Boolean(generated?.used_fallback),
      messages: [
        {
          id: "init",
          role: "ai",
          content: intro,
          timestamp: Date.now(),
        },
      ],
    }));

    setLoading(false);
  };

  const appendMessage = (msg) => {
    setState((prev) => ({ ...prev, messages: [...prev.messages, msg] }));
  };

  const processMessage = async (msgContent, { includeCodeContext = false } = {}) => {
    if (inFlightRef.current) return;
    if (!msgContent || !msgContent.trim()) return;

    inFlightRef.current = true;

    const userMsg = {
      id: `${Date.now()}-u`,
      role: "user",
      content: msgContent,
      timestamp: Date.now(),
    };

    appendMessage(userMsg);
    setInputText("");
    setLoading(true);

    const activeQuestion = questionLabelFor(state);
    const durationSec = estimateDurationSec(msgContent);
    const delaySec = 2;

    const textToAnalyze = buildTechnicalAnswerPayload({
      state,
      answer: msgContent,
      includeCodeContext,
    });

    try {
      const analyzeRes = await analyzeTurn({
        text: textToAnalyze,
        question: activeQuestion,
        durationSec,
        delaySec,
        useAI: true,
      });

      analyzedTurnsRef.current.push({
        question: activeQuestion,
        transcript: msgContent,
        duration: durationSec,
        delay: delaySec,
        metrics: analyzeRes?.local_analysis?.metrics || {},
        filler_words: analyzeRes?.local_analysis?.filler_words || {},
        ai_analysis: analyzeRes?.ai_analysis || null,
        final_score: analyzeRes?.final_score,
      });

      const ui = turnAnalyzeToUI(analyzeRes);
      const nextIndex = state.currentQuestionIndex + 1;
      const next = nextQuestionMessage(state.questions, nextIndex);

      const aiMsg = {
        id: `${Date.now()}-a`,
        role: "ai",
        content: `${ui?.aiText || "Feedback generated."}${next ?next : "That was the final question. Click End Interview for the full report."}`,
        timestamp: Date.now(),
      };

      setState((prev) => ({
        ...prev,
        messages: [...prev.messages, aiMsg],
        currentQuestionIndex: next ? nextIndex : prev.currentQuestionIndex,
        metrics: ui?.metrics || prev.metrics,
      }));
    } catch (e) {
      appendMessage({
        id: `${Date.now()}-e`,
        role: "ai",
        content: `Backend error: ${e?.message || "failed to analyze"}`,
        timestamp: Date.now(),
      });
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  };

  /* ----------------------------- NEW: Draft autosave (localStorage + Redis) ----------------------------- */

  const checkpointDraft = async ({ tab, code, tests, notes, language }) => {
    // Always local first (never lose code)
    const key = storageKeyFor(state.session_id);
    const payload = {
      language,
      code,
      tests,
      notes,
      tab,
      updatedAt: Date.now(),
      topic: state.topic,
      interviewType: state.type,
    };
    try {
      localStorage.setItem(key, JSON.stringify(payload));
    } catch {}

    // Then server checkpoint if we have a session_id
    if (!state.session_id) return;
    try {
      await checkpointCodeDraft({
        session_id: state.session_id,
        key: "draft",
        language,
        code,
        tests,
        notes,
      });
    } catch {
      // silent: localStorage already safe
    }
  };

  const loadDraft = async (sessionId) => {
    const key = storageKeyFor(sessionId);
    // 1) try local
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const draft = JSON.parse(raw);
        if (draft && typeof draft === "object") {
          setState((prev) => ({
            ...prev,
            language: draft.language || prev.language,
            code: typeof draft.code === "string" ? draft.code : prev.code,
            tests: typeof draft.tests === "string" ? draft.tests : prev.tests,
            notes: typeof draft.notes === "string" ? draft.notes : prev.notes,
          }));
          return;
        }
      }
    } catch {}

    // 2) try redis
    if (!sessionId) return;
    try {
      const res = await loadCodeDraft({ session_id: sessionId, key: "draft" });
      const d = res?.draft;
      if (d) {
        setState((prev) => ({
          ...prev,
          language: d.language || prev.language,
          code: typeof d.code === "string" ? d.code : prev.code,
          tests: typeof d.tests === "string" ? d.tests : prev.tests,
          notes: typeof d.notes === "string" ? d.notes : prev.notes,
        }));
      }
    } catch {}
  };

  // when session_id becomes available (after createInterviewSession), pull drafts
  useEffect(() => {
    if (state.status !== InterviewStatus.ACTIVE) return;
    if (state.type !== InterviewType.CODING) return;
    // load even if session_id is null (local draft for "local")
    loadDraft(state.session_id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.session_id, state.status, state.type]);

  /* ----------------------------- NEW: Run / Submit / Reset / Format ----------------------------- */

  const startRunTimer = () => {
    if (runTimerRef.current) clearInterval(runTimerRef.current);
    const start = Date.now();
    runTimerRef.current = setInterval(() => {
      setRunState((prev) => (prev.running ? { ...prev, runningMs: Date.now() - start } : prev));
    }, 120);
  };

  const stopRunTimer = () => {
    if (runTimerRef.current) clearInterval(runTimerRef.current);
    runTimerRef.current = null;
  };

  const handleRunCode = async () => {
    if (loading) return;
    if (state.type !== InterviewType.CODING) return;

    setRunState((prev) => ({ ...prev, running: true, runningMs: 0 }));
    startRunTimer();

    try {
      const res = await runCode({
        session_id: state.session_id,
        language: state.language,
        code: state.code,
        tests: state.tests || "",
      });

      setRunState({ running: false, runningMs: 0, lastRun: res });
    } catch (e) {
      setRunState({
        running: false,
        runningMs: 0,
        lastRun: {
          compile_ok: false,
          run_ok: false,
          stdout: "",
          stderr: e?.message || "Run failed",
          duration_ms: 0,
          tests: [],
        },
      });
    } finally {
      stopRunTimer();
    }
  };

  const handleSubmitCode = async () => {
    if (loading) return;
    if (state.type !== InterviewType.CODING) return;

    const hasCode = String(state.code || "").trim().length > 8;
    if (!hasCode) {
      appendMessage({
        id: `${Date.now()}-empty-code`,
        role: "ai",
        content: "Write your solution first, then submit it as your answer.",
        timestamp: Date.now(),
      });
      return;
    }

    setRunState((prev) => ({ ...prev, running: true, runningMs: 0 }));
    startRunTimer();
    setLoading(true);

    const activeQuestion = questionLabelFor(state);
    const submittedAt = Date.now();

    appendMessage({
      id: `${submittedAt}-u-submit`,
      role: "user",
      content: `Submitted code answer for:
${activeQuestion}`,
      timestamp: submittedAt,
    });

    let submitRes = null;
    let runResult = null;

    try {
      submitRes = await submitCode({
        session_id: state.session_id,
        language: state.language,
        code: state.code,
        tests: state.tests || "",
        notes: state.notes || "",
        question: activeQuestion,
      });

      runResult = normalizeRunResult(submitRes);
      if (runResult) {
        setRunState({ running: false, runningMs: 0, lastRun: runResult });
      } else {
        setRunState((prev) => ({ ...prev, running: false, runningMs: 0 }));
      }
    } catch (e) {
      runResult = {
        compile_ok: false,
        run_ok: false,
        stdout: "",
        stderr: e?.message || "Submit/run failed",
        duration_ms: 0,
        tests: [],
      };
      setRunState({ running: false, runningMs: 0, lastRun: runResult });
    }

    try {
      const answerText = buildTechnicalAnswerPayload({
        state,
        answer: state.notes || "I submitted the code solution in the editor.",
        includeCodeContext: true,
        runResult,
      });

      const analyzeRes = await analyzeTurn({
        text: answerText,
        question: activeQuestion,
        durationSec: estimateDurationSec(`${state.notes || ""} ${state.code || ""}`),
        delaySec: 2,
        useAI: true,
      });

      analyzedTurnsRef.current.push({
        question: activeQuestion,
        transcript: answerText,
        duration: estimateDurationSec(`${state.notes || ""} ${state.code || ""}`),
        delay: 2,
        metrics: analyzeRes?.local_analysis?.metrics || {},
        filler_words: analyzeRes?.local_analysis?.filler_words || {},
        ai_analysis: analyzeRes?.ai_analysis || null,
        final_score: analyzeRes?.final_score,
        run: runResult,
      });

      const ui = turnAnalyzeToUI(analyzeRes);
      const backendCodeFeedback =
        submitRes?.ai_text ||
        submitRes?.feedback ||
        submitRes?.ai_feedback?.detailed_feedback ||
        "";

      const nextIndex = state.currentQuestionIndex + 1;
      const next = nextQuestionMessage(state.questions, nextIndex);

      appendMessage({
        id: `${Date.now()}-a-submit`,
        role: "ai",
        content: [
          backendCodeFeedback,
          ui?.aiText,
          next || "That was the final question. Click End Interview for the full report.",
        ].filter(Boolean).join("\n\n"),
        timestamp: Date.now(),
      });

      setState((prev) => ({
        ...prev,
        currentQuestionIndex: next ? nextIndex : prev.currentQuestionIndex,
        metrics: ui?.metrics || prev.metrics,
      }));
    } catch (e) {
      appendMessage({
        id: `${Date.now()}-a-submit-err`,
        role: "ai",
        content: `AI feedback failed: ${e?.message || "unknown error"}`,
        timestamp: Date.now(),
      });
    } finally {
      stopRunTimer();
      setLoading(false);
    }
  };

  const handleResetCode = () => {
    if (loading) return;
    const ok = window.confirm("Reset your editor draft? This cannot be undone.");
    if (!ok) return;

    setState((prev) => ({
      ...prev,
      code: `// ${prev.topic || "Coding"}\n// Write your solution here.\n\n`,
      tests: "",
      notes: "",
    }));
    setRunState({ running: false, runningMs: 0, lastRun: null });

    // clear local draft
    try {
      localStorage.removeItem(storageKeyFor(state.session_id));
    } catch {}
  };

  const handleFormatCode = async () => {
    if (loading) return;
    if (state.type !== InterviewType.CODING) return;

    // Python => backend black
    if (state.language === "python") {
      try {
        const res = await formatPythonCode({ code: state.code || "" });
        if (res?.code) {
          setState((prev) => ({ ...prev, code: res.code }));
        }
      } catch {}
      return;
    }

    // JS/TS => Prettier (client) if available
    try {
      const prettier = await import("prettier/standalone");
      const babel = await import("prettier/plugins/babel");
      const estree = await import("prettier/plugins/estree");

      const formatted = prettier.format(String(state.code || ""), {
        parser: "babel-ts",
        plugins: [babel, estree],
        semi: true,
        singleQuote: true,
        trailingComma: "es5",
      });

      setState((prev) => ({ ...prev, code: formatted }));
    } catch {
      // If prettier plugins are not bundled in your build, ignore (format is optional)
    }
  };

  const handleSendMessage = () => processMessage(inputText);

  // const handleSubmitCode = () =>
  //   processMessage("I have submitted my solution. Please review my code context.", {
  //     includeCodeContext: true,
  //   });

  const handleEndInterview = async () => {
    if (loading || finalizing) return;

    const turns = analyzedTurnsRef.current || [];
    if (!turns.length) {
      appendMessage({
        id: `${Date.now()}-no-answers`,
        role: "ai",
        content: "Answer at least one generated question before ending the interview.",
        timestamp: Date.now(),
      });
      return;
    }

    // Open the full-screen modal immediately so the user sees the loading state.
    setShowResultsModal(true);
    setFinalizing(true);
    setFinalError("");
    setFinalFeedbackData(null);

    const cleanAnswers = turns.map((t) => {
      const answer = buildFinalAnswerPayload({
        question: t.question,
        answer: t.transcript,
        text: t.transcript,
        transcript: t.transcript,
        duration: t.duration,
        delay: t.delay,
        score: t.final_score,
        feedback: t.ai_analysis?.detailed_feedback || "",
        metrics: {
          clarity: Number(t.metrics?.clarity ?? 0),
          pacing: Number(t.metrics?.pacing ?? 0),
          professionalism: Number(t.metrics?.professionalism ?? 0),
          tone: Number(t.metrics?.tone ?? 0),
          response_time: Number(t.metrics?.response_time ?? 0),
        },
        type: state.type,
        language: state.language,
        code: state.code,
        tests: state.tests,
        notes: state.notes || state.designNotes || "",
        run: t.run || null,
      });

      // Flask final-analysis reads `transcript`, so keep it explicit.
      return {
        ...answer,
        transcript: answer.transcript || answer.answer || answer.text || "",
      };
    });

    const fillerWords = {};
    for (const t of turns) {
      const fw = t.filler_words || {};
      for (const [key, value] of Object.entries(fw)) {
        fillerWords[key] = (fillerWords[key] || 0) + (Number(value) || 0);
      }
    }

    try {
      const finalRes = await finalAnalysis({
        answers: cleanAnswers,
        fillerWords,
        toxicity: null,
        useAI: true,
      });

      const feedbackData = finalAnalysisToFeedbackData(finalRes);
      setFinalFeedbackData(feedbackData);
      setFeedback(feedbackData);

      if (state.interview_id) {
        try {
          await saveInterviewResults({
            interview_id: state.interview_id,
            score: feedbackData.score,
            summary: feedbackData.summary,
            strengths: feedbackData.strengths,
            weaknesses: feedbackData.weaknesses,
            radarData: feedbackData.radarData,
            answers: cleanAnswers,
            raw: finalRes,
          });
        } catch (saveError) {
          console.error("[technical interview] failed to save final results", saveError);
        }
      }
    } catch (error) {
      console.error("[technical interview] final analysis failed", error);
      setFinalError(error?.message || "Could not generate final analysis.");
    } finally {
      setFinalizing(false);
      setLoading(false);
    }
  };

  const resetToDashboard = () => {
    setFeedback(null);
    setInputText("");
    analyzedTurnsRef.current = [];
    setRunState({ running: false, runningMs: 0, lastRun: null });
    
    setState((prev) => ({
      ...prev,
      status: InterviewStatus.IDLE,
      metrics: DEFAULT_METRICS,
      messages: [],
      code: "",
      tests: "",
      notes: "",
      designNotes: "",
      startTime: 0,
      duration: 0,
      interview_id: null,
      session_id: null,
      questions: [],
      currentQuestionIndex: 0,
      generated_questions_id: null,
      questionsUsedFallback: false,
    }));
  };

  // Screens
  if (state.status === InterviewStatus.IDLE) {
    return <Dashboard onStart={handleStartInterview} />;
  }

  if (state.status === InterviewStatus.FEEDBACK && feedback) {
    return <FeedbackReport feedback={feedback} onReset={resetToDashboard} />;
  }


return (
    <AppBackground>
      <div className="flex min-h-screen overflow-hidden">
        {/* Left Rail */}
        <div className="w-[76px] md:w-[92px] px-3 py-6 flex flex-col items-center gap-4">
          <Glass className="w-full p-3 flex items-center justify-center">
            <HeaderIcon className="h-5 w-5 text-white/85" />
          </Glass>

          <div className="w-full">
            <Glass className="p-2">
              <div className="h-[2px] w-full bg-white/10 rounded-full overflow-hidden">
                <div className="h-full bg-white/50" style={{ width: `${progressPct}%` }} />
              </div>
              <div className="mt-2 text-[10px] text-white/55 text-center tracking-widest uppercase">
                ACTIVE • {state.duration}s
              </div>
            </Glass>
          </div>

          <div className="flex-1" />

          <button onClick={handleEndInterview} className="w-full" title="End Interview" disabled={loading}>
            <Glass className="p-3 hover:bg-white/[0.06] transition">
              <div className="flex items-center justify-center">
                <div className="h-9 w-9 rounded-2xl border border-rose-300/20 bg-rose-300/10 flex items-center justify-center">
                  <div className="h-3 w-3 rounded-md bg-rose-200/90" />
                </div>
              </div>
            </Glass>
          </button>
        </div>

        {/* Main Area */}
        <div className="flex-1 flex flex-col md:flex-row min-w-0 mt-20">
          {/* Left Panel (Code / Notes) */}
          {!isDeepDive && (
            <div className="flex-1 min-w-0 h-1/2 md:h-full border-b md:border-b-0 md:border-r border-white/10">
              <div className="h-full p-4">
                <Glass className="h-full overflow-hidden">
                  {state.type === InterviewType.CODING && (
                    <CodeEditor
                      // ✅ upgraded props (no layout change)
                      code={state.code || ""}
                      tests={state.tests || ""}
                      notes={state.notes || ""}
                      onChangeCode={(val) => setState((prev) => ({ ...prev, code: val }))}
                      onChangeTests={(val) => setState((prev) => ({ ...prev, tests: val }))}
                      onChangeNotes={(val) => setState((prev) => ({ ...prev, notes: val }))}
                      language={state.language || "typescript"}
                      setLanguage={(lang) => setState((prev) => ({ ...prev, language: lang }))}
                      onRun={handleRunCode}
                      onSubmit={handleSubmitCode}
                      onReset={handleResetCode}
                      onFormat={handleFormatCode}
                      running={runState.running}
                      runningMs={runState.runningMs}
                      lastRun={runState.lastRun}
                      onCheckpoint={checkpointDraft}
                      readOnly={loading}
                    />
                  )}

                  {state.type === InterviewType.SYSTEM_DESIGN && (
                    <div className="h-full flex flex-col">
                      <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent flex items-center justify-between">
                        <div>
                          <div className="text-sm font-semibold tracking-tight text-white/85">System Notes</div>
                          <div className="text-xs text-white/55 mt-1">Markdown supported</div>
                        </div>
                        <Pill tone="sky">Architecture</Pill>
                      </div>
                      <textarea
                        value={state.designNotes || ""}
                        onChange={(e) => setState((prev) => ({ ...prev, designNotes: e.target.value }))}
                        className="flex-1 bg-transparent text-white/80 p-6 outline-none resize-none font-mono text-[13px] leading-6 placeholder:text-white/30"
                        placeholder="# Architecture Overview

- Requirements
- High-level design
- Data model
- APIs
- Scaling + failure modes
- Trade-offs
"
                      />
                    </div>
                  )}
                </Glass>
              </div>
            </div>
          )}

          {/* Right Panel (Chat) */}
          <div className={`${isDeepDive ? "w-full" : "w-full md:w-[460px] lg:w-[520px]"} h-1/2 md:h-full p-4`}>
            <ChatPanel
              title={state.topic ? `${state.topic}` : "Interview Chat"}
              subtitle={
                state.type === InterviewType.SYSTEM_DESIGN
                  ? "System design coaching"
                  : state.type === InterviewType.CODING
                  ? "Realtime analysis + code context"
                  : "Deep dive reasoning + clarity scoring"
              }
              messages={state.messages}
              input={inputText}
              setInput={setInputText}
              onSend={handleSendMessage}
              isLoading={loading}
              metrics={state.metrics}
              onEnd={handleEndInterview}
              disabled={loading}
            />
<FinalResultsModal
  open={showResultsModal}
  loading={finalizing}
  data={finalFeedbackData}
  error={finalError}
  onClose={() => setShowResultsModal(false)}
  onReviewAnswers={() => {
    setShowResultsModal(false);
    if (finalFeedbackData) {
      setFeedback(finalFeedbackData);
      setState((prev) => ({ ...prev, status: InterviewStatus.FEEDBACK }));
    }
  }}
  onGoToDashboard={resetToDashboard}
/>
          </div>

        </div>
      </div>
    </AppBackground>
  );
}




